/**
 * Admin Lease Approval Modal
 * Allows the Mesh Box Super Admin to review pending ParticipationProposals and issue bounded MeshCapacityLeases.
 */

import React, { useState } from 'react';
import type { ParticipationProposal, ResourceGrant } from '../../mesh/compute/capacity-lease-contracts';

interface AdminLeaseApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingProposals: ParticipationProposal[];
  onApproveProposal: (proposalId: string, grantedGrant: ResourceGrant, ttlHours: number) => void;
  onRejectProposal: (proposalId: string, reason: string) => void;
}

export const AdminLeaseApprovalModal: React.FC<AdminLeaseApprovalModalProps> = ({
  isOpen,
  onClose,
  pendingProposals,
  onApproveProposal,
  onRejectProposal,
}) => {
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(
    pendingProposals[0]?.proposalId || null
  );
  const [grantedCpu, setGrantedCpu] = useState(50);
  const [grantedMemoryGb, setGrantedMemoryGb] = useState(4);
  const [ttlHours, setTtlHours] = useState(24);

  if (!isOpen) return null;

  const selectedProposal = pendingProposals.find((p) => p.proposalId === selectedProposalId) || pendingProposals[0];

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 8, 16, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          width: '580px',
          background: 'linear-gradient(145deg, #111827, #0b0f19)',
          border: '1px solid rgba(167, 139, 250, 0.4)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          color: '#f8fafc',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>👑</span>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Mesh Helper Lease Governance</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {pendingProposals.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>✨</div>
            <div style={{ fontSize: '13px', fontWeight: 600 }}>No Pending Helper Proposals</div>
            <div style={{ fontSize: '11px', marginTop: '4px' }}>All node capacity leases are currently up to date.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Proposal Selector */}
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
              {pendingProposals.map((p) => (
                <button
                  key={p.proposalId}
                  onClick={() => {
                    setSelectedProposalId(p.proposalId);
                    setMaxDefaults(p);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: (selectedProposal?.proposalId === p.proposalId) ? '1px solid #a78bfa' : '1px solid rgba(255,255,255,0.08)',
                    background: (selectedProposal?.proposalId === p.proposalId) ? 'rgba(167, 139, 250, 0.15)' : 'rgba(255,255,255,0.03)',
                    color: (selectedProposal?.proposalId === p.proposalId) ? '#a78bfa' : '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  💻 {p.displayName || p.workerNodeId.slice(0, 12)}
                </button>
              ))}
            </div>

            {selectedProposal && (
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '8px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc' }}>
                      {selectedProposal.displayName}
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>
                      DID: {selectedProposal.workerNodeId}
                    </div>
                  </div>
                  <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, alignSelf: 'center' }}>
                    ROLE: TASK-WORKER
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px', marginBottom: '14px' }}>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Offered CPU:</span>{' '}
                    <strong style={{ color: '#34d399' }}>{selectedProposal.resourceCaps.maxCpuPercent}%</strong>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Offered RAM:</span>{' '}
                    <strong style={{ color: '#34d399' }}>{selectedProposal.resourceCaps.maxMemoryBytes / (1024 * 1024 * 1024)} GB</strong>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Battery Pause:</span>{' '}
                    <strong style={{ color: '#f59e0b' }}>&le; {selectedProposal.pauseOnBatteryPercent || 20}%</strong>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Preemption:</span>{' '}
                    <strong style={{ color: '#a78bfa' }}>{selectedProposal.pauseOnInteractiveUsage ? 'Instant Active' : 'Passive'}</strong>
                  </div>
                </div>

                {/* Admin Granted Ceilings */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', marginBottom: '8px' }}>
                    Super Admin Granted Resource Ceilings
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '10px', color: '#94a3b8' }}>Granted CPU: {grantedCpu}%</label>
                      <input
                        type="range"
                        min="10"
                        max={selectedProposal.resourceCaps.maxCpuPercent}
                        value={grantedCpu}
                        onChange={(e) => setGrantedCpu(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#a78bfa' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '10px', color: '#94a3b8' }}>Lease Duration: {ttlHours}h</label>
                      <input
                        type="range"
                        min="1"
                        max="72"
                        value={ttlHours}
                        onChange={(e) => setTtlHours(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#a78bfa' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button
                type="button"
                onClick={() => {
                  if (selectedProposal) onRejectProposal(selectedProposal.proposalId, 'Admin rejected');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#fca5a5',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reject Proposal
              </button>
              <button
                type="button"
                onClick={() => {
                  if (selectedProposal) {
                    onApproveProposal(
                      selectedProposal.proposalId,
                      {
                        maxCpuPercent: grantedCpu,
                        maxMemoryBytes: grantedMemoryGb * 1024 * 1024 * 1024,
                        maxDiskStorageBytes: 0,
                        allowedBackends: selectedProposal.resourceCaps.allowedBackends,
                      },
                      ttlHours
                    );
                  }
                }}
                style={{
                  flex: 2,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ⚡ Approve & Issue Bounded Lease
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function setMaxDefaults(p: ParticipationProposal) {
    setGrantedCpu(p.resourceCaps.maxCpuPercent);
    setGrantedMemoryGb(Math.round(p.resourceCaps.maxMemoryBytes / (1024 * 1024 * 1024)) || 4);
  }
};
